import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import Home from "./pages/Home";
import HomeLogado from "./pages/HomeLogado";
import HomeCommandCenter from "./pages/home_command_center";
import AreaCorretor from "./pages/AreaCorretor";
import SDRVirtual from "./pages/SDRVirtual";
import AssistenteVendas from "./pages/AssistenteVendas";
import AssistentePosVenda from "./pages/AssistentePosVenda";
import SuporteConteudo from "./pages/SuporteConteudo";
import Suporte from "./pages/Suporte";
import { canAccessSuporte } from "./features/suporte/access";
import EditarSenha from "./pages/EditarSenha";
import RequireAuth from "./components/RequireAuth";
import AppShell from "./components/AppShell";
import ChatPage from "./pages/ChatPage";
import CadastroUsuarios from "./pages/CadastroUsuarios";
import EditarUsuario from "./pages/EditarUsuario";
import Configuracoes from "./pages/Configuracoes";
import Agentes from "./pages/Agentes";
import Integracoes from "./pages/Integracoes";
import GovernancaAuditoria from "./pages/GovernancaAuditoria";
import Contratos from "./pages/Contratos";
import Cobranca, { CobrancaAvisos, CobrancaCargas, CobrancaConfiguracao, CobrancaInadimplentes, CobrancaRedirect, CobrancaTitulos, CobrancaWhatsapp } from "./pages/Cobranca";
import TaskAgentRedirect from "./pages/TaskAgentRedirect";
import TaskAgentInicio from "./pages/TaskAgentInicio";
import TaskAgentPastas from "./pages/TaskAgentPastas";
import TaskAgentAjuda from "./pages/TaskAgentAjuda";
import AipoApp from "./features/aipo/AipoApp";
import { canAccessAipoPreview } from "./features/aipo/access";
import {
  AipoArtifactDetail,
  AipoArtifacts,
  AipoChat,
  AipoConnections,
  AipoHome,
  AipoKnowledge,
  AipoNewChat,
  AipoNotFound,
  AipoProjectDetail,
  AipoProjects,
  AipoSubscription,
} from "./features/aipo/AipoPages";
import { canAccessAipimIncorp } from "./utils/aipimIncorpAccess";
import { canAccessAgenteContabilidade } from "./utils/agenteContabilidadeAccess";
import { canUseNovaEstruturaPortal } from "./utils/novaEstruturaPortal";
import useContratosAtivos from "./hooks/useContratosAtivos";
import AipimIncorpRedirect, {
  AipimIncorpEmpreendimentoDetalhe,
  AipimIncorpEmpreendimentos,
  AipimIncorpInicio,
  AipimIncorpNovoEmpreendimento,
  AipimIncorpProcessos,
  AipimIncorpVisaoGeral,
} from "./pages/AipimIncorp";
import AgenteContabilidadeRedirect, {
  AgenteContabilidadeAjuda,
  AgenteContabilidadeCarteiraRecebiveis,
  AgenteContabilidadeCustos,
  AgenteContabilidadeDespesas,
  AgenteContabilidadeDreViabilidade,
  AgenteContabilidadeImportacao,
  AgenteContabilidadeImpostos,
  AgenteContabilidadeVendas,
  AgenteContabilidadeVisaoGeral,
} from "./features/agenteContabilidade/pages/AgenteContabilidade";
import GestaoCustosShell from "./features/gestaoCustos/components/GestaoCustosShell";
import { canAccessGestaoCustos } from "./features/gestaoCustos/access";
import { canAccessGestaoEquipamentos } from "./features/gestaoEquipamentos/access";
import {
  AnaliseInsumosPage,
  CausasAcoesPage,
  ComparativoObrasPage,
  ComprasEntregasPage,
  DesviosPage,
  EstoqueControlePage,
  MaoDeObraPage,
  PrazosPage,
  TendenciaPage,
  VisaoGeralPage,
} from "./features/gestaoCustos/pages/GestaoCustosPages";

const GestaoEquipamentosApp = lazy(() => import("./features/gestaoEquipamentos"));

const GestaoEquipamentosRoute = () => canAccessGestaoEquipamentos()
  ? <Suspense fallback={<Box sx={{ p: 5, textAlign: "center" }}><CircularProgress /></Box>}><GestaoEquipamentosApp /></Suspense>
  : <Navigate to="/home_logado" replace />;

const AipimIncorpRoute = ({ children }) =>
  canAccessAipimIncorp() ? children : <Navigate to="/home_logado" replace />;

const AgenteContabilidadeRoute = ({ children }) =>
  canAccessAgenteContabilidade() ? children : <Navigate to="/home_logado" replace />;

const NovaEstruturaPortalRoute = ({ children }) =>
  canUseNovaEstruturaPortal() ? children : <Navigate to="/home_logado" replace />;

const GestaoCustosRoute = ({ children }) =>
  canAccessGestaoCustos() ? children : <Navigate to="/home_logado" replace />;

const AipoPreviewRoute = ({ children }) =>
  canAccessAipoPreview() ? children : <Navigate to="/home_logado" replace />;

const SuporteRoute = ({ children }) =>
  canAccessSuporte() ? children : <Navigate to="/home_logado" replace />;

const ProductRoute = ({ productId, children }) => {
  const { contratosAtivos, loading } = useContratosAtivos();

  if (loading) {
    return (
      <Box sx={{ minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasProductAccess = contratosAtivos.some(
    (contrato) => Number(contrato?.idProduto ?? contrato?.id_produto) === productId && contrato?.ativo !== false
  );

  return hasProductAccess ? children : <Navigate to="/home_logado" replace />;
};

const HomeLogadoRoute = () =>
  canUseNovaEstruturaPortal() ? <HomeCommandCenter /> : <HomeLogado />;

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/cvcrm" element={<Home />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/home_logado" element={<HomeLogadoRoute />} />
          <Route path="/suporte" element={<SuporteRoute><Suporte /></SuporteRoute>} />
          <Route path="/area-corretor" element={<AreaCorretor />} />
          <Route path="/sdr" element={<ProductRoute productId={2}><SDRVirtual /></ProductRoute>} />
          <Route path="/assistente-vendas" element={<ProductRoute productId={1}><AssistenteVendas /></ProductRoute>} />
          <Route path="/assistente-pos-venda" element={<ProductRoute productId={5}><AssistentePosVenda /></ProductRoute>} />
          <Route path="/assistente-conteudo" element={<ProductRoute productId={3}><SuporteConteudo /></ProductRoute>} />
          <Route path="/agentes" element={<NovaEstruturaPortalRoute><Agentes /></NovaEstruturaPortalRoute>} />
          <Route path="/integracoes" element={<NovaEstruturaPortalRoute><Integracoes /></NovaEstruturaPortalRoute>} />
          <Route path="/governanca/auditoria" element={<NovaEstruturaPortalRoute><GovernancaAuditoria /></NovaEstruturaPortalRoute>} />
          <Route path="/contratos" element={<NovaEstruturaPortalRoute><Contratos /></NovaEstruturaPortalRoute>} />
          <Route path="/cobranca" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaRedirect /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/visao-geral" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><Cobranca /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/configuracao" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaConfiguracao /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/avisos" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaAvisos /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/whatsapp" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaWhatsapp /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/inadimplentes" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaInadimplentes /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/titulos" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaTitulos /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/cobranca/cargas" element={<ProductRoute productId={6}><NovaEstruturaPortalRoute><CobrancaCargas /></NovaEstruturaPortalRoute></ProductRoute>} />
          <Route path="/task-agent" element={<ProductRoute productId={7}><TaskAgentRedirect /></ProductRoute>} />
          <Route path="/task-agent/inicio" element={<ProductRoute productId={7}><TaskAgentInicio /></ProductRoute>} />
          <Route path="/task-agent/pastas" element={<ProductRoute productId={7}><TaskAgentPastas /></ProductRoute>} />
          <Route path="/task-agent/ajuda" element={<ProductRoute productId={7}><TaskAgentAjuda /></ProductRoute>} />
          <Route path="/agentecontabilidade" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeRedirect /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/visao-geral" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeVisaoGeral /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/dre-viabilidade" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeDreViabilidade /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/vendas" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeVendas /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/carteira-recebiveis" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeCarteiraRecebiveis /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/custos" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeCustos /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/impostos" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeImpostos /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/despesas" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeDespesas /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/importacao" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeImportacao /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/agentecontabilidade/ajuda" element={<ProductRoute productId={9}><AgenteContabilidadeRoute><AgenteContabilidadeAjuda /></AgenteContabilidadeRoute></ProductRoute>} />
          <Route path="/gestao-custos" element={<NovaEstruturaPortalRoute><GestaoCustosRoute><GestaoCustosShell /></GestaoCustosRoute></NovaEstruturaPortalRoute>}>
            <Route index element={<Navigate to="visao-geral" replace />} />
            <Route path="visao-geral" element={<VisaoGeralPage />} />
            <Route path="desvios" element={<DesviosPage />} />
            <Route path="tendencia" element={<TendenciaPage />} />
            <Route path="causas-acoes" element={<CausasAcoesPage />} />
            <Route path="prazos" element={<PrazosPage />} />
            <Route path="estoque-compras/controle" element={<EstoqueControlePage />} />
            <Route path="estoque-compras/entregas" element={<ComprasEntregasPage />} />
            <Route path="estoque-compras/insumos" element={<AnaliseInsumosPage />} />
            <Route path="mao-de-obra" element={<MaoDeObraPage />} />
            <Route path="comparativo-obras" element={<ComparativoObrasPage />} />
          </Route>
          <Route path="/gestao-equipamentos/*" element={<GestaoEquipamentosRoute />} />
          <Route path="/aipim-incorp" element={<AipimIncorpRoute><AipimIncorpRedirect /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/visao-geral" element={<AipimIncorpRoute><AipimIncorpVisaoGeral /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/inicio" element={<AipimIncorpRoute><AipimIncorpInicio /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/empreendimentos" element={<AipimIncorpRoute><AipimIncorpEmpreendimentos /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/empreendimentos/novo" element={<AipimIncorpRoute><AipimIncorpNovoEmpreendimento /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/empreendimentos/:id" element={<AipimIncorpRoute><AipimIncorpEmpreendimentoDetalhe /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/empreendimentos/:id/:tab" element={<AipimIncorpRoute><AipimIncorpEmpreendimentoDetalhe /></AipimIncorpRoute>} />
          <Route path="/aipim-incorp/processos" element={<AipimIncorpRoute><AipimIncorpProcessos /></AipimIncorpRoute>} />
          <Route path="/EditarSenha" element={<EditarSenha />} />
          <Route path="/CadastroUsuarios" element={<CadastroUsuarios />} />
          <Route path="/EditarUsuario" element={<EditarUsuario />} />
          <Route path="/configuracoes" element={<Configuracoes />} />
        </Route>
        <Route
          path="/aipo"
          element={<RequireAuth><AipoPreviewRoute><AipoApp /></AipoPreviewRoute></RequireAuth>}
        >
          <Route index element={<AipoHome />} />
          <Route path="novo" element={<AipoNewChat />} />
          <Route path="projetos" element={<AipoProjects />} />
          <Route path="projetos/:projectId" element={<AipoProjectDetail />} />
          <Route path="chats/:chatId" element={<AipoChat />} />
          <Route path="artefatos" element={<AipoArtifacts />} />
          <Route path="artefatos/:artifactId" element={<AipoArtifactDetail />} />
          <Route path="conhecimento" element={<AipoKnowledge />} />
          <Route path="conexoes" element={<AipoConnections />} />
          <Route path="assinatura" element={<AipoSubscription />} />
          <Route path="*" element={<AipoNotFound />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
